/**
 * Tiny fixed-layout struct writer.
 *
 * WGSL uniform blocks follow strict alignment rules (vec2 on 8 bytes, vec4 on 16,
 * struct size rounded up to 16). Rather than generate layouts, each shader
 * documents its byte offsets in a header comment and the matching operator fills
 * them in through this class - a mismatch then shows up as one obviously wrong
 * offset instead of a silently shifted field.
 */
export class Struct {
  readonly bytes: Uint8Array;
  readonly #f32: Float32Array;
  readonly #i32: Int32Array;
  readonly #u32: Uint32Array;

  constructor(byteLength: number) {
    if (byteLength % 16 !== 0) {
      throw new Error(`[gpu-core] uniform struct size ${byteLength} must be a multiple of 16`);
    }
    const buffer = new ArrayBuffer(byteLength);
    this.bytes = new Uint8Array(buffer);
    this.#f32 = new Float32Array(buffer);
    this.#i32 = new Int32Array(buffer);
    this.#u32 = new Uint32Array(buffer);
  }

  /** Writes f32 values starting at `offset` bytes. */
  f(offset: number, ...values: number[]): this {
    this.#f32.set(values, offset >> 2);
    return this;
  }

  /** Writes i32 values starting at `offset` bytes. */
  i(offset: number, ...values: number[]): this {
    this.#i32.set(values, offset >> 2);
    return this;
  }

  /** Writes u32 values starting at `offset` bytes. */
  u(offset: number, ...values: number[]): this {
    this.#u32.set(values, offset >> 2);
    return this;
  }
}
